import { Box3, Matrix4, Vector3 } from "three";
import { OBB } from "three/examples/jsm/math/OBB.js";
import {
  CONFIGURATOR_DATA,
  CONFIGURATOR_MODEL_TYPE,
  JOINT_TYPE,
  MODEL_PART_TYPE,
} from "../configurator/ConfiguratorData";
import {
  alignJoints,
  fetchMinDistance,
  getPartTypeMatch,
  getPossibleBindableJoints,
  realignJoints,
} from "../util/JointUtil";
import { disposeObject3DDeep } from "../util/GeometryUtil";
import { isConfiguratorRequiredJointType } from "../util/JointTypes";
import { logger } from "../util/Logger";
import { SugarModel } from "./SugarModel";
import { SugarModelGroup } from "./SugarModelGroup";
import { SugarModelJoint } from "./SugarModelJoint";
import { createMaterial } from "./SugarModelLoader";
import { tr } from "src/translation/tr";

type ConfiguratorEvent = {
  baseJoint: SugarModelJoint;
  tailJoint: SugarModelJoint;
  operations?: any[];
  models: SugarModel[];
};

type SideJointSlot = {
  joint: SugarModelJoint;
  data: JOINT_TYPE;
};

type LoadedSidePart = {
  data: JOINT_TYPE;
  sideModel: SugarModel;
};

type LoadedMaterial = {
  code: string;
  material: ReturnType<typeof createMaterial>;
};

type PreloadedAssets = {
  sideModels: LoadedSidePart[];
  materials: LoadedMaterial[];
  mergeEvents: ConfiguratorEvent[];
};

function obbCorner(obb: OBB, sx: -1 | 1, sy: -1 | 1, sz: -1 | 1): Vector3 {
  return new Vector3(
    sx * obb.halfSize.x,
    sy * obb.halfSize.y,
    sz * obb.halfSize.z,
  )
    .applyMatrix3(obb.rotation)
    .add(obb.center);
}

export class SugarModelConfigurator extends SugarModel {
  private _models: SugarModel[] = [];
  private _sideModels: SugarModel[] = [];

  private _unbindedJoints: SugarModelJoint[] = [];
  private _sideConfig: Map<string, MODEL_PART_TYPE> = new Map();
  private _mainConfig: Map<string, MODEL_PART_TYPE> = new Map();

  private _events: ConfiguratorEvent[] = [];
  private _mergeEventLoadCache = new Map<
    string,
    Promise<ConfiguratorEvent | undefined>
  >();

  get materialConfig() {
    return this._materialConfig;
  }

  get mainConfig() {
    return this._mainConfig;
  }

  get sideConfig() {
    return this._sideConfig;
  }

  get sideModels() {
    return this._sideModels;
  }

  private _materialConfig: Map<string, { code: string; material: any }> =
    new Map();

  private _configData?: CONFIGURATOR_MODEL_TYPE;

  get configData(): CONFIGURATOR_MODEL_TYPE {
    return this._configData ?? CONFIGURATOR_DATA;
  }

  constructor(
    mainConfig: Map<string, MODEL_PART_TYPE>,
    sideConfig: Map<string, MODEL_PART_TYPE>,
    materialConfig: Map<string, { code: string; material: any }>,
    configData?: CONFIGURATOR_MODEL_TYPE,
  ) {
    super({});
    this.name = "Configurator";
    this._mainConfig = mainConfig;
    this._sideConfig = sideConfig;
    this._materialConfig = materialConfig;
    this._configData = configData;
  }

  private _collisionModels(): SugarModel[] {
    const seen = new Set<string>();
    const models = [
      ...this._models,
      ...this._sideModels,
      ...this._events.flatMap((event) => event.models),
    ];

    return models.filter((model) => {
      const key = model.uuid ?? String(model.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  override getCollisionLocalObbs(): OBB[] {
    this.updateMatrixWorld(true);
    const toConfiguratorLocal = new Matrix4().copy(this.matrixWorld).invert();
    const childToConfigurator = new Matrix4();
    const obbs: OBB[] = [];

    for (const model of this._collisionModels()) {
      model.updateMatrixWorld(true);
      childToConfigurator.multiplyMatrices(
        toConfiguratorLocal,
        model.matrixWorld,
      );

      for (const localObb of model.getCollisionLocalObbs()) {
        const obb = localObb.clone();
        obb.applyMatrix4(childToConfigurator);
        (obb as any).model = (localObb as any).model ?? model;
        (obb as any).sourceModel = model;
        (obb as any).configurator = this;
        obbs.push(obb);
      }
    }

    return obbs.length > 0 ? obbs : super.getCollisionLocalObbs();
  }

  override getCollisionLocalObb(): OBB {
    const obbs = this.getCollisionLocalObbs();
    if (obbs.length === 0) return super.getCollisionLocalObb();

    const box = new Box3();
    for (const obb of obbs) {
      box.expandByPoint(obbCorner(obb, -1, -1, -1));
      box.expandByPoint(obbCorner(obb, -1, -1, 1));
      box.expandByPoint(obbCorner(obb, -1, 1, -1));
      box.expandByPoint(obbCorner(obb, -1, 1, 1));
      box.expandByPoint(obbCorner(obb, 1, -1, -1));
      box.expandByPoint(obbCorner(obb, 1, -1, 1));
      box.expandByPoint(obbCorner(obb, 1, 1, -1));
      box.expandByPoint(obbCorner(obb, 1, 1, 1));
    }

    if (box.isEmpty()) return super.getCollisionLocalObb();

    const broad = new OBB();
    const size = new Vector3();
    box.getCenter(broad.center);
    box.getSize(size);
    broad.halfSize.copy(size.multiplyScalar(0.5));
    (broad as any).model = this;
    return broad;
  }

  override getCollisionWorldObbs(): OBB[] {
    return this.getCollisionLocalObbs().map((obb) => {
      const worldObb = obb.clone();
      worldObb.applyMatrix4(this.matrixWorld);
      (worldObb as any).model = (obb as any).model ?? this;
      if ((obb as any).sourceModel) {
        (worldObb as any).sourceModel = (obb as any).sourceModel;
      }
      (worldObb as any).configurator = this;
      return worldObb;
    });
  }

  groupConnectedSugarModels(
    models: SugarModel[],
    events: ConfiguratorEvent[],
  ): SugarModel[][] {
    const idToModel = new Map<number, SugarModel>();
    const adjacency = new Map<number, Set<number>>();

    for (const model of models) {
      idToModel.set(model.id, model);
      adjacency.set(model.id, new Set());
    }

    for (const event of events) {
      const a = event.baseJoint.model.id;
      const b = event.tailJoint.model.id;

      adjacency.get(a)?.add(b);
      adjacency.get(b)?.add(a);
    }

    const visited = new Set<number>();
    const groups: SugarModel[][] = [];

    for (const model of models) {
      if (visited.has(model.id)) continue;

      const group: SugarModel[] = [];
      const stack = [model.id];

      while (stack.length > 0) {
        const currentId = stack.pop()!;
        if (visited.has(currentId)) continue;

        visited.add(currentId);
        const currentModel = idToModel.get(currentId);
        if (currentModel) group.push(currentModel);

        for (const neighborId of adjacency.get(currentId) ?? []) {
          if (!visited.has(neighborId)) {
            stack.push(neighborId);
          }
        }
      }

      groups.push(group);
    }

    return groups;
  }

  get products() {
    const groupedModels = this.groupConnectedSugarModels(
      this._models,
      this._events,
    );

    const result = [
      ...groupedModels.map((models) =>
        models
          .map((model) => [
            model,
            ...(model.joints
              ?.filter((j) => j.freeze && j.bindedJoint && j.visibility)
              .map((j) => j.bindedJoint?.model) || []),
          ])
          .flat(),
      ),
      this._sideModels,
      ...this._events.map((e) => e.models),
      //this._legModels || [],
    ];

    return result;
  }

  get models() {
    return this._models;
  }

  get joints() {
    return this._models.flatMap((model) => model.joints || []);
  }

  private _mergeHappened:
    | {
        distance: number;
        base: any;
        tail: any;
      }
    | undefined;

  private _merging = false;

  addModel(addModel: SugarModel) {
    this.addModelWithoutComplete(addModel);
    this.complete();
  }

  addModelWithoutComplete(addModel: SugarModel, alignDistance: number = 1) {
    this._mergeHappened = fetchMinDistance(addModel, [this]);
    const minModel = this._mergeHappened;
    if (minModel && minModel.distance < alignDistance) {
      alignJoints(minModel!.base, minModel!.tail);
      minModel.base.bindJoint(minModel.tail);
    }

    if (
      minModel &&
      minModel.distance < 1 &&
      addModel.userData.info.replaceType == 1
    ) {
      minModel.base.model.attach(addModel);
    } else {
      this.attach(addModel);
    }

    this.__addModel(addModel);
  }

  async completeSync() {
    //console.log("Configurator complete", this._dirty, this);
    if (!this._dirty) return;
    this._dirty = false;

    await this.configureWithPreloadedAssets();
  }

  complete() {
    //console.log("Configurator complete", this._dirty, this);
    if (!this._dirty) return;
    this._dirty = false;

    void this.configureWithPreloadedAssets();

    //this.haveJoints();
  }

  private async preloadAssets(): Promise<PreloadedAssets> {
    const [, sideModels, materials, mergeEvents] = await Promise.all([
      this.loadMainPart(this._mainConfig),
      this.loadSidePartModels(this._sideConfig),
      this.loadMaterials(this._materialConfig),
      this.loadMergeEvents(),
    ]);

    return {
      sideModels,
      materials,
      mergeEvents,
    };
  }

  private async configureWithPreloadedAssets() {
    const assets = await this.preloadAssets();

    await this.mergePossibleJoints(assets.mergeEvents);
    await this.alignSidePartModels(assets.sideModels);
    await this.mergePendingJoint(assets.mergeEvents);

    this.applyLoadedMaterials(assets.materials, ...this._models);
    this.applyLoadedMaterials(assets.materials, ...this._sideModels);
    this.applyLoadedMaterials(
      assets.materials,
      ...this._events.map((e) => e.models).flat(),
    );

    this.dispatchEvent({ type: "configured" as any });
  }

  grabModel(model: SugarModel) {
    this.havePossibleJoints(model);
    this.__removeModel(model);
    //this.possesJoints();
  }

  private isSameJoint(a: SugarModelJoint, b: SugarModelJoint) {
    return (
      a === b ||
      (a.model === b.model &&
        a.jointId === b.jointId &&
        a.inType === b.inType &&
        a.outType === b.outType)
    );
  }

  private isSameMergeEvent(
    event: ConfiguratorEvent,
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
  ) {
    return (
      (this.isSameJoint(event.baseJoint, baseJoint) &&
        this.isSameJoint(event.tailJoint, tailJoint)) ||
      (this.isSameJoint(event.tailJoint, baseJoint) &&
        this.isSameJoint(event.baseJoint, tailJoint))
    );
  }

  private mergeEventKey(
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
  ) {
    const keyOf = (joint: SugarModelJoint) =>
      [joint.model?.uuid, joint.jointId, joint.inType, joint.outType].join(":");
    return [keyOf(baseJoint), keyOf(tailJoint)].sort().join("|");
  }

  private hasMergeEvent(
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
  ) {
    return this._events.find((e) =>
      this.isSameMergeEvent(e, baseJoint, tailJoint),
    );
  }

  private resolveJoint(joint: SugarModelJoint) {
    return (
      joint.model?.joints?.find((candidate) =>
        this.isSameJoint(candidate, joint),
      ) ?? joint
    );
  }

  private getMergeEventDefinition(
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
  ) {
    const baseId = baseJoint.model.userData.info.id;
    const tailId = tailJoint.model.userData.info.id;

    const items = [baseId, tailId].sort((a, b) => a - b);
    return this.configData.configurator.events?.find(
      (e) =>
        e.when.type === "merged" &&
        e.when.items.length === items.length &&
        [...e.when.items].sort().join(",") === [...items].sort().join(","),
    );
  }

  private async loadMergeEvent(
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
  ): Promise<ConfiguratorEvent | undefined> {
    if (this.hasMergeEvent(baseJoint, tailJoint)) return;

    const key = this.mergeEventKey(baseJoint, tailJoint);
    const cached = this._mergeEventLoadCache.get(key);
    if (cached) return cached;

    const event = this.getMergeEventDefinition(baseJoint, tailJoint);
    if (!event) return;

    const loadPromise = (async () => {
      const operations = await Promise.all(
        (event.operations ?? []).map(async (operation) => {
          if (operation.action === "add") {
            const type = operation.parameters?.type!;
            const modelId = this._mainConfig
              .get(type)
              ?.joints?.find((j) => j.type === type)?.match;
            const modelData = this.configData.models.find(
              (m) => m.id == modelId,
            );
            const modelObject = modelData
              ? await new SugarModel(modelData).load(modelData, {
                  forceSync: true,
                })
              : undefined;

            return {
              return: modelObject,
              parameters: { base: baseJoint, tail: tailJoint },
              operation: operation,
            };
          }

          return {
            return: undefined,
            parameters: undefined,
            operation: operation,
          };
        }),
      );

      return {
        baseJoint,
        tailJoint,
        operations,
        models: operations
          .map((operation) => operation.return)
          .filter((model): model is SugarModel => !!model),
      };
    })();

    this._mergeEventLoadCache.set(key, loadPromise);
    return loadPromise;
  }

  private findLoadedMergeEvent(
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
    mergeEvents: ConfiguratorEvent[],
  ) {
    return mergeEvents.find((event) =>
      this.isSameMergeEvent(event, baseJoint, tailJoint),
    );
  }

  private async loadMergeEvents(): Promise<ConfiguratorEvent[]> {
    const pairs = [
      ...(this._unbindedJoints.length > 1
        ? [
            {
              baseJoint: this._unbindedJoints[0],
              tailJoint: this._unbindedJoints[1],
            },
          ]
        : []),
      ...(this._mergeHappened && !this._merging
        ? [
            {
              baseJoint: this._mergeHappened.base,
              tailJoint: this._mergeHappened.tail,
            },
          ]
        : []),
    ];
    const uniquePairs = Array.from(
      new Map(
        pairs.map((pair) => [
          this.mergeEventKey(pair.baseJoint, pair.tailJoint),
          pair,
        ]),
      ).values(),
    );

    return (
      await Promise.all(
        uniquePairs.map((pair) =>
          this.loadMergeEvent(pair.baseJoint, pair.tailJoint),
        ),
      )
    ).filter((event): event is ConfiguratorEvent => !!event);
  }

  private applyMergeEvent(eventInstance: ConfiguratorEvent | undefined) {
    if (!eventInstance) return;

    const baseJoint = this.resolveJoint(eventInstance.baseJoint);
    const tailJoint = this.resolveJoint(eventInstance.tailJoint);

    if (this.hasMergeEvent(baseJoint, tailJoint)) return;
    this._mergeEventLoadCache.delete(this.mergeEventKey(baseJoint, tailJoint));

    const baseType = baseJoint.outType;
    const models: SugarModel[] = [];

    eventInstance.operations?.forEach((operationItem) => {
      if (operationItem.operation.action === "visible") {
        const baseJoints = baseJoint.model.joints?.filter(
          (j) => j.tags.includes(baseType) && j.bindedJoint,
        );
        baseJoints?.forEach((j) => (j.visibility = false));

        const tailType = tailJoint.outType;
        const tailJoints = tailJoint.model.joints?.filter(
          (j) => j.tags.includes(tailType) && j.bindedJoint,
        );
        tailJoints?.forEach((j) => (j.visibility = false));
      } else if (operationItem.operation.action === "add") {
        const modelObject = operationItem.return as SugarModel | undefined;
        if (!modelObject) return;

        const basePosition = new Vector3();
        baseJoint.updateWorldMatrix(true, true);
        baseJoint.model.getWorldPosition(basePosition);

        const tailPosition = new Vector3();
        tailJoint.updateWorldMatrix(true, true);
        tailJoint.model.getWorldPosition(tailPosition);

        const centerPosition = basePosition
          .clone()
          .add(tailPosition)
          .multiplyScalar(0.5);

        modelObject.position.copy(centerPosition);
        modelObject.position.setY(0);

        if (operationItem.operation.parameters?.offset) {
          modelObject.position.setX(
            modelObject.position.x +
              operationItem.operation.parameters.offset[0],
          );
          modelObject.position.setY(
            modelObject.position.y +
              operationItem.operation.parameters.offset[1],
          );
          modelObject.position.setZ(
            modelObject.position.z +
              operationItem.operation.parameters.offset[2],
          );
        }

        baseJoint.model.attach(modelObject);
        models.push(modelObject);
      }
    });

    this._events.push({
      baseJoint,
      tailJoint,
      operations: eventInstance.operations,
      models,
    });
  }

  private reposition() {
    const mainModels = this._models?.filter(
      (m) => m.userData.info.replaceType == 0,
    );
    const models = [
      ...(mainModels.length > 0 ? mainModels : this._models),
      ...this._sideModels,
    ];
    if (models.length < 1) return;

    models.forEach((m) => m.detach());

    const centerPosition = models
      .reduce(
        (position, model) =>
          position.add(
            new Vector3(model.position.x, this.position.y, model.position.z),
          ),
        new Vector3(),
      )
      .multiplyScalar(1.0 / models.length);
    this.position.copy(centerPosition);
    this.updateWorldMatrix(true, true);

    models.forEach((m) => this.attach(m));
  }

  private havePossibleJoints(model: SugarModel) {
    this._unbindedJoints = getPossibleBindableJoints(model);
    //this._unbindedJoints?.forEach((joint) => joint.unbindJoint());
  }

  private async mergePossibleJoints(mergeEvents: ConfiguratorEvent[] = []) {
    if (this._unbindedJoints.length > 1) {
      const baseJoint = this.resolveJoint(this._unbindedJoints[0]);
      const tailJoint = this.resolveJoint(this._unbindedJoints[1]);

      alignJoints(baseJoint, tailJoint);
      baseJoint.bindJoint(tailJoint);

      realignJoints(tailJoint.model as SugarModel, tailJoint);

      const eventInstance = this.findLoadedMergeEvent(
        this._unbindedJoints[0],
        this._unbindedJoints[1],
        mergeEvents,
      );
      this.applyMergeEvent(eventInstance);
    }

    this._unbindedJoints = [];
  }

  private async mergePendingJoint(mergeEvents: ConfiguratorEvent[] = []) {
    if (!this._mergeHappened || this._merging) return;

    this._merging = true;
    try {
      const eventInstance = this.findLoadedMergeEvent(
        this._mergeHappened.base,
        this._mergeHappened.tail,
        mergeEvents,
      );

      this.applyMergeEvent(eventInstance);
      this._mergeHappened = undefined;
    } finally {
      this._merging = false;
    }
  }

  private _dirty = true;
  private __addModel(model: SugarModel) {
    model.configurator = this;
    if (!this._models.includes(model)) this._models.push(model);
    //this.possesJoints();
    this._dirty = true;
  }

  private __removeModel(model: SugarModel) {
    this._models = this._models.filter((m) => m != model);

    model.joints
      ?.filter((j) => j && j.bindedJoint)
      .forEach((j) => {
        this.unbindEvents(j, j.bindedJoint!);
      });

    model.joints?.forEach((j) => {
      if (
        j.model == model &&
        j.binded &&
        !j.freeze &&
        isConfiguratorRequiredJointType(j.inType)
      ) {
        j.unbindJoint();
      }
      j.model = model;
    });

    model.configurator = undefined;
    model.detach();
    this._dirty = true;
  }

  private unbindEvents(baseJoint: SugarModelJoint, tailJoint: SugarModelJoint) {
    const eventInstance = this._events.find(
      (e) =>
        (e.baseJoint == baseJoint && e.tailJoint == tailJoint) ||
        (e.tailJoint == baseJoint && e.baseJoint == tailJoint),
    );

    if (eventInstance) {
      eventInstance.operations?.forEach((operationItem) => {
        if (operationItem.operation.action === "visible") {
          const baseType = baseJoint.outType;

          console.log("visible açılıyor", {
            baseType: baseType,
            baseJoint: baseJoint,
            tailJoint: tailJoint,
          });
          const baseJoints = baseJoint.model.joints?.filter(
            (j) => j.tags.includes(baseType) && j.bindedJoint,
          );
          baseJoints?.forEach((j) => (j.visibility = true));

          const tailType = tailJoint.outType;
          const tailJoints = tailJoint.model.joints?.filter(
            (j) => j.tags.includes(tailType) && j.bindedJoint,
          );
          tailJoints?.forEach((j) => (j.visibility = true));
        } else if (operationItem.operation.action === "add") {
          //
          operationItem.return?.removeFromParent();
        }
      });
      eventInstance.operations = [];
    }

    eventInstance?.models.forEach((model) => {
      model.removeFromParent();
    });

    this._events = this._events.filter((e) => e != eventInstance);
  }

  removeModel(model: SugarModel) {
    this.havePossibleJoints(model);
    this.__removeModel(model);
    this.complete();
    this.reposition();
  }

  /**
   *
   * @param Materials
   *
   *
   */

  private async loadMaterials(
    materialConfig: Map<string, { code: string; material: any }>,
  ): Promise<LoadedMaterial[]> {
    this._materialConfig = materialConfig;
    const materials = await Promise.all(
      Array.from(materialConfig?.values() ?? []).map(async (materialConf) => {
        if (!materialConf.material) {
          logger.warn("material is undefined", materialConf.code);
          return;
        }
        const material = createMaterial(materialConf.material);
        await material.load();

        return {
          code: materialConf.code,
          material,
        };
      }),
    );

    return materials.filter(
      (material): material is LoadedMaterial => !!material,
    );
  }

  private applyLoadedMaterials(
    loadedMaterials: LoadedMaterial[],
    ...inputModels: SugarModel[]
  ) {
    const models =
      inputModels.length > 0
        ? inputModels
        : [
            ...this._models,
            ...this._sideModels,
            ...this._events.map((e) => e.models),
          ].flat();

    loadedMaterials.forEach((materialConf) => {
      const codes = materialConf.code.split(";");
      models?.forEach((model) => {
        model.partMaterialGroups?.forEach((part) => {
          if (codes.includes(part.code)) {
            part.apply(model, materialConf.material);
          }
        });

        model.joints?.forEach((joint) => {
          const jointModel = joint.bindedJoint?.model;
          if (jointModel && model.hasIt(jointModel!)) {
            jointModel?.partMaterialGroups?.forEach((part) => {
              if (codes.includes(part.code)) {
                part.apply(jointModel, materialConf.material);
              }
            });
          }
        });
      });
    });
  }

  async applyMaterial(
    materialConfig: Map<string, { code: string; material: any }>,
    ...inputModels: SugarModel[]
  ) {
    const materials = await this.loadMaterials(materialConfig);
    this.applyLoadedMaterials(materials, ...inputModels);
    return materials;
  }

  private _getEmptyJoints(): SideJointSlot[] {
    return Array.from(this._sideConfig.values())
      .filter((value) => value !== undefined)
      .flatMap((value) => {
        return value.joints.flatMap((jointdata) => {
          return this._models.flatMap((model) =>
            model
              .joints!.filter(
                (joint) =>
                  joint.inType === jointdata.type &&
                  !joint.binded &&
                  !joint.model?.userData?.info?.jointInfo?.excludes?.includes(
                    jointdata.id,
                  ),
              )
              .map((joint) => {
                return {
                  joint: joint,
                  data: jointdata,
                };
              }),
          );
        });
      });
  }

  private getSideModelConfig(jointData: JOINT_TYPE) {
    if (typeof jointData.match !== "number") {
      logger.warn(
        "[SugarModelConfigurator.changeSidePart] side joint match number değil",
        {
          match: jointData.match,
          type: jointData.type,
        },
      );
      return undefined;
    }

    return this.configData.models.find((model) => model.id === jointData.match);
  }

  private async loadSideModelForSlot(
    emptyJointSlot: SideJointSlot,
    useCache = true,
  ): Promise<LoadedSidePart | null> {
    const modelConfig = this.getSideModelConfig(emptyJointSlot.data);

    if (!modelConfig) {
      logger.warn("[SugarModelConfigurator.changeSidePart] modelConfig yok", {
        emptyJointSlotMatch: emptyJointSlot.data.match,
        emptyJointType: emptyJointSlot.data.type,
      });
      return null;
    }

    let sideModel = useCache
      ? this._sideModels.find(
          (cachedModel) => cachedModel.data.id === modelConfig.id,
        )
      : undefined;

    if (!sideModel) {
      sideModel = await new SugarModel(modelConfig).load(modelConfig, {
        forceSync: true,
      });
    }

    if (!sideModel) {
      logger.warn(
        "[SugarModelConfigurator.changeSidePart] sideModel yüklenemedi",
        {
          modelConfigId: modelConfig.id,
        },
      );
      return null;
    }

    return {
      data: emptyJointSlot.data,
      sideModel,
    };
  }

  private async loadSidePartModels(
    sideConfig: Map<string, MODEL_PART_TYPE>,
  ): Promise<LoadedSidePart[]> {
    this._sideConfig = sideConfig;
    //console.log("changeSidePart", sideConfig);
    logger.info("changeSidePart", sideConfig);

    const emptyjoints = this._getEmptyJoints();

    return (
      await Promise.all(
        emptyjoints.map((emptyJointSlot) =>
          this.loadSideModelForSlot(emptyJointSlot),
        ),
      )
    ).filter((m): m is LoadedSidePart => m != null);
  }

  private async alignSidePartModels(loadedSideParts: LoadedSidePart[]) {
    this._sideModels.forEach((part) => {
      part?.removeFromParent();
    });

    const emptyjoints = this._getEmptyJoints();
    const usedLoadedIndexes = new Set<number>();
    const alignedModels: SugarModel[] = [];

    for (const emptyJointSlot of emptyjoints) {
      let loadedIndex = loadedSideParts.findIndex(
        (loadedSidePart, index) =>
          !usedLoadedIndexes.has(index) &&
          loadedSidePart.data.type === emptyJointSlot.data.type &&
          loadedSidePart.data.match === emptyJointSlot.data.match,
      );

      let loadedSidePart =
        loadedIndex >= 0 ? loadedSideParts[loadedIndex] : undefined;

      if (!loadedSidePart) {
        const fallbackSidePart = await this.loadSideModelForSlot(
          emptyJointSlot,
          false,
        );
        loadedIndex = loadedSideParts.length;
        if (fallbackSidePart) {
          loadedSidePart = fallbackSidePart;
          loadedSideParts.push(fallbackSidePart);
        }
      }

      if (!loadedSidePart) continue;
      usedLoadedIndexes.add(loadedIndex);

      const sideModel = loadedSidePart.sideModel;
      const modelConfig = this.getSideModelConfig(emptyJointSlot.data);
      const tailJoint = sideModel.joints?.find(
        (j) => j.outType == emptyJointSlot.data.type,
      );

      const jointOutTypes = sideModel.joints?.map((j) => j.outType) ?? [];

      if (!tailJoint) {
        logger.warn(
          "[SugarModelConfigurator.changeSidePart] tailJoint bulunamadı (alignJoints atlanıyor)",
          {
            beklenenOutType: emptyJointSlot.data.type,
            modelId: modelConfig?.id,
            modelName: modelConfig?.name,
            mevcutJointOutTypes: jointOutTypes,
            baseJoint: {
              inType: emptyJointSlot.joint.inType,
              outType: emptyJointSlot.joint.outType,
            },
          },
        );
        continue;
      }

      sideModel.detach();
      sideModel.removeFromParent();
      sideModel.position.set(0, 0, 0);
      sideModel.rotation.set(0, 0, 0);
      sideModel.updateWorldMatrix(true, true);
      alignJoints(emptyJointSlot.joint, tailJoint, true);
      this.attach(sideModel);
      tailJoint.model.freeze = true;
      alignedModels.push(sideModel);
    }

    this._sideModels = alignedModels;
  }

  async changeSidePart(sideConfig: Map<string, MODEL_PART_TYPE>) {
    const loadedSideParts = await this.loadSidePartModels(sideConfig);
    await this.alignSidePartModels(loadedSideParts);
  }

  private async loadMainPart(partConfig: Map<string, MODEL_PART_TYPE>) {
    const allModels = this._models;
    this._mainConfig = partConfig;

    await Promise.all([
      ...allModels
        .filter((m) => m.userData.info.replaceType === 0)
        .map(async (m) => {
          await (m as SugarModelGroup).load({
            data: m.data,
            partConfig: partConfig,
          });
        }),
      ...this._events
        .map((e) => e.models)
        .flat()
        .map(async (m) => {
          await (m as SugarModel).load(
            this.configData.models.find(
              (m) => m.id == getPartTypeMatch(partConfig.get("legIn")!, 0),
            ),
            {
              forceSync: true,
              forceAgain: true,
            },
          );
        }),
    ]);
  }

  async changeMainPart(partConfig: Map<string, MODEL_PART_TYPE>) {
    await this.loadMainPart(partConfig);

    if (this._sideConfig.size > 0) {
      await this.changeSidePart(this._sideConfig);
    }
  }

  override dispose(): void {
    for (const model of this._models) {
      model?.dispose();
      model?.removeFromParent();
    }
    this._models = [];
    for (const model of this._sideModels) {
      model?.dispose();
      model?.removeFromParent();
    }
    this._sideModels = [];
    disposeObject3DDeep(this);
    super.dispose();
  }
}
